import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import {
  PaginatedBudgetProposals,
  Proposal,
  ProposalDetails,
} from 'src/assets/types/interface/proposal.interface';
import { map, Observable, tap } from 'rxjs';
import { TimedRequestCache } from 'src/app/services/timed-request-cache';
import {
  Budget,
  BudgetDecisionResult,
} from 'src/assets/types/interface/budget.interface';
import { BudgetChangeRequest } from 'src/assets/types/interface/budget-change-request.interface';
import { AiBudgetGenerationStatusResponse } from 'src/assets/types/interface/ai-budget-generation-status.interface';

export interface BudgetDecisionFilePayload {
  url: string;
  fileName?: string;
  description?: string;
}

export interface BudgetDecisionPayload {
  reason?: string;
  description?: string;
  files?: BudgetDecisionFilePayload[];
}

@Injectable({
  providedIn: 'root',
})
export class BudgetTakerService {
  private readonly apiUrl = environment.WO_API_URL;
  private readonly proposalDetailsCache = new TimedRequestCache<Proposal>();
  private readonly versionDetailsCache = new TimedRequestCache<Budget>();
  private readonly proposalBudgetDetailsCache =
    new TimedRequestCache<ProposalDetails>();

  constructor(private http: HttpClient) {}

  getAllProposalsByPage(
    take: number,
    page: number,
    filters?: {
      date?: string | null;
      dateFrom?: string;
      dateTo?: string;
      number?: string;
      statusList?: string[];
    },
  ) {
    const access_token = localStorage.getItem('access_token') ?? '';
    let params = new HttpParams();

    if (filters?.date) {
      params = params.set('date', filters.date);
    }
    if (filters?.dateFrom) params = params.set('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params = params.set('dateTo', filters.dateTo);

    if (filters?.number) {
      params = params.set('number', filters.number);
    }

    if (filters?.statusList?.length) {
      filters.statusList.forEach((status) => {
        params = params.append('status', status);
      });
    }

    return this.http.get<PaginatedBudgetProposals>(
      `${this.apiUrl}/budget/taker/limit/${take}/page/${page}`,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
        params,
      },
    );
  }

  getAllProposalByTenant() {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.http.get<Proposal[]>(`${this.apiUrl}/budget/taker/`, {
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    });
  }

  getProposalVersions(proposalUuid: string): Observable<Proposal> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.proposalDetailsCache.get(
      `${access_token}:${proposalUuid}`,
      () =>
        this.http.get<Proposal>(
          `${this.apiUrl}/budget/taker/versions/${proposalUuid}`,
          {
            headers: {
              Authorization: `Bearer ${access_token}`,
            },
          },
        ),
    );
  }

  getDraftVersionWithAI(proposalUuid: string): Observable<Budget> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.http.get<Budget>(
      `${this.apiUrl}/budget/taker/${proposalUuid}/draft-version/true`,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      },
    );
  }

  getDraftVersionWithoutAI(proposalUuid: string): Observable<Budget> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.http.get<Budget>(
      `${this.apiUrl}/budget/taker/${proposalUuid}/draft-version/false`,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      },
    );
  }

  getVersionDetails(budgetUuid: string): Observable<Budget> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.versionDetailsCache.get(`${access_token}:${budgetUuid}`, () =>
      this.http.get<Budget>(
        `${this.apiUrl}/budget/taker/details/${budgetUuid}`,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      ),
    );
  }

  getProposalDetails(
    proposalUuid: string,
    budgetUuid: string,
  ): Observable<ProposalDetails> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.proposalBudgetDetailsCache
      .get(`${access_token}:${proposalUuid}`, () =>
        this.http.get<ProposalDetails>(
          `${this.apiUrl}/budget/taker/${proposalUuid}/details/latest`,
          { headers: { Authorization: `Bearer ${access_token}` } },
        ),
      )
      .pipe(
        map((response) => ({
          ...response,
          selectedBudget:
            budgetUuid === 'latest'
              ? response.selectedBudget
              : ((response.budgets ?? []).find(
                  (budget) => budget.uuid === budgetUuid,
                ) ?? response.selectedBudget),
        })),
      );
  }

  counterOffer(data: any, budgetUuid: string) {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.invalidateDetailCachesAfter(
      this.http.put<BudgetChangeRequest>(
        `${this.apiUrl}/budget/taker/${budgetUuid}/counter-offer`,
        data,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      ),
    );
  }

  approveBudget(
    budgetUuid: string,
    decision: string | BudgetDecisionPayload = '',
  ) {
    const access_token = localStorage.getItem('access_token') ?? '';
    const payload =
      typeof decision === 'string'
        ? {
            ...(decision?.trim() ? { reason: decision.trim() } : {}),
          }
        : {
            ...(decision.reason?.trim()
              ? { reason: decision.reason.trim() }
              : {}),
            ...(decision.description?.trim()
              ? { description: decision.description.trim() }
              : {}),
            ...(decision.files?.length ? { files: decision.files } : {}),
          };

    return this.invalidateDetailCachesAfter(
      this.http.put<BudgetDecisionResult>(
        `${this.apiUrl}/budget/taker/${budgetUuid}/approve`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      ),
    );
  }

  reproveBudget(budgetUuid: string, reason?: string) {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.invalidateDetailCachesAfter(
      this.http.put<BudgetDecisionResult>(
        `${this.apiUrl}/budget/taker/${budgetUuid}/reprove`,
        {
          ...(reason?.trim() ? { reason: reason.trim() } : {}),
        },
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      ),
    );
  }

  getCounterOffer(budgetUuid: string): Observable<BudgetChangeRequest> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.http.get<BudgetChangeRequest>(
      `${this.apiUrl}/budget/taker/${budgetUuid}/counter-offerr`,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      },
    );
  }

  submitVersion(categories: any, proposalUuid: string, budgetUuid?: string) {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.invalidateDetailCachesAfter(
      this.http.post(
        `${this.apiUrl}/budget/taker/self-managed/submit`,
        {
          uuid: budgetUuid || undefined,
          proposalUuid,
          categories: categories.categories,
        },
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      ),
    );
  }

  createProposalVersion(
    data: any,
    proposalUuid: string,
    budgetUuid?: string,
  ): Observable<Budget> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.invalidateDetailCachesAfter(
      this.http.post<Budget>(
        `${this.apiUrl}/budget/taker/${proposalUuid}/draft`,
        {
          ...data,
          uuid: budgetUuid || undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      ),
    );
  }

  requestAiBudgetGeneration(
    proposalUuid: string,
  ): Observable<AiBudgetGenerationStatusResponse> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.invalidateDetailCachesAfter(
      this.http.post<AiBudgetGenerationStatusResponse>(
        `${this.apiUrl}/budget/taker/${proposalUuid}/generate-ai-budget`,
        {},
        {
          headers: {
            Authorization: `Bearer ${access_token}`,
          },
        },
      ),
    );
  }

  getAiBudgetGenerationStatus(
    proposalUuid: string,
  ): Observable<AiBudgetGenerationStatusResponse> {
    const access_token = localStorage.getItem('access_token') ?? '';
    return this.http.get<AiBudgetGenerationStatusResponse>(
      `${this.apiUrl}/budget/taker/${proposalUuid}/generate-ai-budget/status`,
      {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      },
    );
  }

  private invalidateDetailCachesAfter<T>(
    request: Observable<T>,
  ): Observable<T> {
    return request.pipe(
      tap(() => {
        this.proposalDetailsCache.clear();
        this.versionDetailsCache.clear();
        this.proposalBudgetDetailsCache.clear();
      }),
    );
  }
}
